import { useTranslations } from "@zapier/i18n";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Account,
  Member,
  Team,
  useAccount,
  useQuerySearchMembers,
  useQuerySearchTeams,
} from "@zapier/client";
import { Avatar, Key, Typeahead, TypeaheadItem } from "@zapier/design-system";
import { isMember, isAccount, isTeam } from "./sharingTypeGuards";

import styles from "./AddShareTypeaheadItem.module.css";

type TypeaheadProps = React.ComponentProps<typeof Typeahead>;
type InheritedProps = Omit<
  TypeaheadProps,
  | "children"
  | "defaultItems"
  | "inputValue"
  | "isBusy"
  | "items"
  | "onInputChange"
  | "onSelectionChange"
>;

export interface ShareSubject {
  id: string;
  /** A readable name for the selected item */
  name: string;
  /** The type of the selected item */
  type: "account" | "team" | "user" | "accountadmin";
}

export interface AddShareTypeaheadProps extends InheritedProps {
  /** Whether to show an option for sharing with everyone on the account */
  allowSharingWithEveryone: boolean;
  /** Whether to allow searching for teams */
  allowTeamSearch: boolean;
  /** Whether to allow searching for account admins */
  allowSharingWithAdmins: boolean;
  /** User IDs to show as disabled in the results list (e.g. the asset owner). */
  disabledUserIds?: string[];
  /** Callback when the selection changes. Returns undefined when the selection is cleared. */
  onSelectionChange: (selection?: ShareSubject) => void;
  /** Search members/teams on this account (e.g. parent org when sharing from a workspace). */
  searchAccountId?: string;
  /** When set, "Everyone at …" and account-admin rows use this account (e.g. parent org on the Organization tab). */
  everyoneOptionAccount?: {
    id: number;
    name?: string | null;
    logoUrl?: string | null;
  };
}

/**
 * Placeholder plan row for "Everyone at …" when using `everyoneOptionAccount`.
 * Matches monolith plan shape so `planInfo.allowTeams` etc. are defined if future code reads them.
 */
const SYNTHETIC_EVERYONE_ROW_PLAN_INFO: NonNullable<Account["planInfo"]> = {
  addons: undefined,
  allowCompanies: false,
  allowImportExport: false,
  allowProfessional: false,
  allowTeams: true,
  amount: undefined,
  hasAnnualTaskLimit: false,
  id: 0,
  interval: undefined,
  meteredTaskPricing: false,
  name: undefined,
};

function useDebouncedState<T>(value: T, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [delay, value]);
  return debouncedValue;
}

interface AddShareTypeaheadItemWrapperProps {
  avatarUrl: string | null | undefined;
  id: string | number;
  isDisabled?: boolean;
  name: string;
  text: React.ReactNode;
}

function AddShareTypeaheadItemWrapper({
  avatarUrl,
  id,
  isDisabled,
  name,
  text,
}: AddShareTypeaheadItemWrapperProps) {
  return (
    <TypeaheadItem id={id} textValue={name} isDisabled={isDisabled}>
      <div className={styles.item}>
        <span className={styles.avatar}>
          <Avatar size="small" name={name} url={avatarUrl} />
        </span>

        <span className={styles.text}>{text}</span>
      </div>
    </TypeaheadItem>
  );
}

/** Creates a unique ID for Typeahead items to prevent collisions between entity types */
function createTypeaheadItemId(
  type: "account" | "accountadmin" | "team" | "user",
  id: string | number,
): string {
  return `${type}-${id}`;
}

function AddShareTypeaheadItem({
  item,
  isDisabled,
  type,
}: {
  item: Account | Member | Team;
  isDisabled?: boolean;
  type: "account" | "accountadmin" | "team" | "user";
}) {
  const t = useTranslations("add-share-typeahead");

  // Render members
  if (isMember(item)) {
    return (
      <AddShareTypeaheadItemWrapper
        avatarUrl={item.photoUrl}
        id={createTypeaheadItemId("user", item.id)}
        isDisabled={isDisabled}
        name={item.fullName}
        text={
          <>
            {item.fullName}{" "}
            {/* eslint-disable-next-line react/jsx-no-literals */}
            <span className={styles.detail}>({item.email})</span>
          </>
        }
      />
    );
  }

  // Render accounts - "Share with everyone on the account"
  if (isAccount(item) && type === "account") {
    const text = item.name
      ? t.rich("sharing-options.everyone-at", {
          accountName: item.name,
          prefix: (chunks) => <span className={styles.detail}>{chunks}</span>,
        })
      : t("sharing-options.everyone-on-account");

    return (
      <AddShareTypeaheadItemWrapper
        avatarUrl={item.logoUrl}
        id={createTypeaheadItemId("account", item.id)}
        name={item.name ?? "Account"}
        text={text}
      />
    );
  }

  // Render accounts - "Share with account admins"
  if (isAccount(item) && type === "accountadmin") {
    const text = item.name
      ? t.rich("sharing-options.account-admins-at", {
          accountName: item.name,
          prefix: (chunks) => <span className={styles.detail}>{chunks}</span>,
        })
      : t("sharing-options.account-admins-on-account");

    return (
      <AddShareTypeaheadItemWrapper
        avatarUrl={item.logoUrl}
        id={createTypeaheadItemId("accountadmin", item.id)}
        name={t("sharing-options.account-admins-name")}
        text={text}
      />
    );
  }

  // Render teams
  if (isTeam(item) && type === "team") {
    return (
      <AddShareTypeaheadItemWrapper
        avatarUrl={item.logo}
        id={createTypeaheadItemId("team", item.id)}
        name={item.name}
        text={
          <>
            {item.name}{" "}
            <span className={styles.detail}>
              {t("sharing-options.team-member-count", {
                count: item.memberCount,
              })}
            </span>
          </>
        }
      />
    );
  }

  return;
}

export function AddShareTypeahead({
  allowSharingWithEveryone = true,
  allowSharingWithAdmins = false,
  allowTeamSearch = true,
  disabledUserIds,
  onSelectionChange,
  searchAccountId,
  everyoneOptionAccount,
  ...props
}: AddShareTypeaheadProps) {
  const [inputValue, setInputValue] = useState("");
  const searchValue = useDebouncedState(inputValue);

  const { account, isLoading: isAccountLoading } = useAccount({
    enabled:
      (allowSharingWithEveryone || allowSharingWithAdmins) &&
      !everyoneOptionAccount,
  });

  const accountForEveryoneRow = useMemo((): Account | undefined => {
    if (everyoneOptionAccount) {
      return {
        id: everyoneOptionAccount.id,
        name: everyoneOptionAccount.name,
        logoUrl: everyoneOptionAccount.logoUrl ?? undefined,
        planInfo: { ...SYNTHETIC_EVERYONE_ROW_PLAN_INFO },
      } as Account;
    }
    return account;
  }, [everyoneOptionAccount, account]);

  const typeaheadCollectionKey = [
    searchAccountId ?? "",
    everyoneOptionAccount?.id ?? "",
    accountForEveryoneRow?.id ?? "",
  ].join(":");

  const { members, isLoading: areMembersLoading } = useQuerySearchMembers({
    allowTeams: true, // This does not search teams, but allows searching members
    search: searchValue,
    accountId: searchAccountId,
  });

  const { teams, isLoading: areTeamsLoading } = useQuerySearchTeams({
    search: searchValue,
    teamsSearchEnabled: allowTeamSearch,
    accountId: searchAccountId,
  });

  const membersAndTeams = useMemo(() => {
    const allItems = [...members, ...teams];

    return allItems.sort((a, b) => {
      const nameA = isMember(a) ? a.fullName : a.name;
      const nameB = isMember(b) ? b.fullName : b.name;
      return nameA.localeCompare(nameB);
    });
  }, [members, teams]);

  const disabledUserIdSet = useMemo(() => {
    return new Set((disabledUserIds ?? []).map(String));
  }, [disabledUserIds]);

  const handleSelectionChange = useCallback(
    (id: Key | null) => {
      if (!id) {
        return onSelectionChange?.();
      }

      // Parse the prefixed ID to extract the type and original ID
      const idString = String(id);
      const separatorIndex = idString.indexOf("-");
      if (separatorIndex === -1) {
        return onSelectionChange?.();
      }

      const typePrefix = idString.slice(0, separatorIndex);
      const originalId = idString.slice(separatorIndex + 1);

      // Handle "Account admins" option
      if (typePrefix === "accountadmin" && accountForEveryoneRow) {
        return onSelectionChange?.({
          id: originalId,
          name: "Account admins",
          type: "accountadmin",
        });
      }

      // Handle "Everyone" option
      if (typePrefix === "account" && accountForEveryoneRow) {
        return onSelectionChange?.({
          id: originalId,
          name: accountForEveryoneRow.name ?? "Account",
          type: "account",
        });
      }

      // Handle team selection
      if (typePrefix === "team") {
        const selectedTeam = membersAndTeams.find(
          (item) => isTeam(item) && String(item.id) === originalId,
        );
        if (selectedTeam && isTeam(selectedTeam)) {
          return onSelectionChange?.({
            id: originalId,
            name: selectedTeam.name,
            type: "team",
          });
        }
        return onSelectionChange?.();
      }

      // Handle user/member selection
      if (typePrefix === "user") {
        const selectedMember = membersAndTeams.find(
          (item) => isMember(item) && String(item.id) === originalId,
        );
        if (!selectedMember || !isMember(selectedMember)) {
          return onSelectionChange?.();
        }

        // Prevent selecting disabled users (e.g. the asset owner).
        if (disabledUserIdSet.has(originalId)) {
          return onSelectionChange?.();
        }

        return onSelectionChange?.({
          id: originalId,
          name: selectedMember.fullName,
          type: "user",
        });
      }

      return onSelectionChange?.();
    },
    [
      accountForEveryoneRow,
      disabledUserIdSet,
      membersAndTeams,
      onSelectionChange,
    ],
  );

  return (
    <Typeahead
      key={typeaheadCollectionKey}
      menuTrigger="focus"
      size="medium"
      {...props}
      inputValue={inputValue}
      isBusy={
        (everyoneOptionAccount ? false : isAccountLoading) ||
        areMembersLoading ||
        areTeamsLoading
      }
      onInputChange={setInputValue}
      onChange={handleSelectionChange}
      // Uses either the default width or a minimum of ~40 characters
      menuWidth="calc(max(var(--trigger-width), 40ch))"
    >
      {allowSharingWithEveryone && accountForEveryoneRow && (
        <AddShareTypeaheadItem item={accountForEveryoneRow} type="account" />
      )}
      {allowSharingWithAdmins && accountForEveryoneRow && (
        <AddShareTypeaheadItem
          item={accountForEveryoneRow}
          type="accountadmin"
        />
      )}
      {membersAndTeams.map((item) => {
        const itemType = isMember(item) ? "user" : "team";
        return (
          <AddShareTypeaheadItem
            key={createTypeaheadItemId(itemType, item.id)}
            item={item}
            isDisabled={
              isMember(item) && disabledUserIdSet.has(String(item.id))
            }
            type={itemType}
          />
        );
      })}
    </Typeahead>
  );
}
